import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import SkeletonHeroChartIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-hero-chart.svg?raw';
import SkeletonTableIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-table.svg?raw';
import HotelBenchmarkInsightChartsHeroChart from 'lumen/components/hotel/benchmark-insight/charts/hero-chart';
import HotelBenchmarkInsightHeroChartAxisButtons from 'lumen/components/hotel/benchmark-insight/hero-chart-axis-buttons';
import HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateToolbox from 'lumen/components/hotel/benchmark-insight/sections/section-stay-date/section-stay-date-toolbox';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import HotelBenchmarkInsightTablesBeniTable from 'lumen/components/hotel/benchmark-insight/tables/beni-table';
import type {
  BeniBreakdown,
  BeniMeta,
  BeniMetaComparisonSet,
} from 'lumen/models';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BENI_STAY_DATE_SECTION_VIEWS,
  type BeniStayDateSectionView,
  BREAKDOWNS,
  type HeroChartAxisOption,
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';

import styles from './section-stay-date.module.css';

interface HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateSignature {
  Args: {
    breakdownsByComparisonSet: Record<ComparisonSet, BeniBreakdown[]>;
    changeSelectedAxisOption: (axisOption: string) => void;
    changeSelectedKpi: (kpi: Kpi) => void;
    dateAggregation: DateAggregationType;
    dayBreakdownsByComparisonSet: Partial<
      Record<ComparisonSet, BeniBreakdown[]>
    >;
    meta: BeniMeta;
    pickupTranslation: string;
    primaryComparisonSetType: ComparisonSet;
    rangeChanged: () => void;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetType: ComparisonSet;
    selectedAxisOption: HeroChartAxisOption;
    selectedKpi: Kpi;
    selectedRange: {
      end: Dayjs;
      start: Dayjs;
    };
    setDateAggregation: (dateAggregation: DateAggregationType) => void;
    setDayDateAggregation: () => void;
    setStayDateSectionView: (view: BeniStayDateSectionView) => void;
    showSkeleton?: boolean;
    showSkeletonAnimation?: boolean;
    stayDateSectionView: BeniStayDateSectionView;
    weekBreakdownsByComparisonSet: Partial<
      Record<ComparisonSet, BeniBreakdown[]>
    >;
  };
}

export default class HotelBenchmarkInsightSectionsSectionStayDateSectionStayDate extends Component<HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateSignature> {
  @service('benchmark-insight/beni-table-service')
  declare benchmarkInsightBeniTableService: Services['benchmark-insight/beni-table-service'];
  @service declare intl: Services['intl'];

  breakdown = BREAKDOWNS.STAY_DATE;

  exportStayDateTableTask = task({}, async (): Promise<void> => {
    const { dateAggregation, selectedRange } = this.args;
    const { columns, rows, breakdown } = this;

    await this.benchmarkInsightBeniTableService.exportTableTask.perform({
      columns,
      rows,
      breakdown,
      dateAggregation,
      dateRange: selectedRange,
    });
  });

  // In the case of stay date section, we need to choose between day or week segmentations, depending on the selected `dateAggregation`.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownsByComparisonSetByDateAggregation() {
    const {
      dayBreakdownsByComparisonSet,
      weekBreakdownsByComparisonSet,
      dateAggregation,
    } = this.args;

    if (dateAggregation === DATE_AGGREGATION.WEEK) {
      return weekBreakdownsByComparisonSet;
    }

    return dayBreakdownsByComparisonSet;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = this.benchmarkInsightBeniTableService.getColumns({
      primaryComparisonSetType: this.args.primaryComparisonSetType,
      secondaryComparisonSetType: this.args.secondaryComparisonSetType,
      ownComparisonSetMeta: this.ownComparisonSetMeta,
      primaryComparisonSetMeta: this.primaryComparisonSetMeta,
      secondaryComparisonSetMeta: this.secondaryComparisonSetMeta,
      breakdown: this.breakdown,
      dateAggregation: this.args.dateAggregation,
      selectedRange: this.args.selectedRange,
      referencePeriod: this.args.referencePeriod,
      visibleKpis: this.visibleKpis,
    });

    return columns;
  }

  get isChartView(): boolean {
    return this.args.stayDateSectionView === BENI_STAY_DATE_SECTION_VIEWS.CHART;
  }

  get isOccupancySelected(): boolean {
    return this.args['selectedKpi'] === KPIS.OCCUPANCY;
  }

  get kpiDiffVsComparisonSetTranslation(): string {
    const { primaryComparisonSetType } = this.args;

    return this.intl.t('benchmark_insight.hero_chart.kpi_diff_vs_reference', {
      kpi: this.intl.t(KPI_LONG_TRANSLATIONS[this.oppositeKpi]),
      reference: this.intl.t(
        BENI_COMPARISON_SET_LONG_TRANSLATIONS[primaryComparisonSetType],
      ),
    });
  }

  get oppositeKpi(): Kpi {
    return this.isOccupancySelected ? KPIS.ADR : KPIS.OCCUPANCY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownComparisonSetBreakdowns() {
    const { breakdownsByComparisonSetByDateAggregation } = this;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSetByDateAggregation,
      BENI_COMPARISON_SETS.OWN,
    );
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryComparisonSetBreakdowns() {
    const { breakdownsByComparisonSetByDateAggregation } = this;
    const { primaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSetByDateAggregation,
      primaryComparisonSetType,
    );
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rows = this.benchmarkInsightBeniTableService.getRows(
      this.ownComparisonSetBreakdowns,
      this.primaryComparisonSetBreakdowns,
      this.secondaryComparisonSetBreakdowns,
      [],
      this.breakdown,
    );

    return rows;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get secondaryComparisonSetBreakdowns() {
    const { breakdownsByComparisonSetByDateAggregation } = this;
    const { secondaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSetByDateAggregation,
      secondaryComparisonSetType,
    );
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  get visibleKpis(): Kpi[] {
    return Object.values(KPIS).filter((kpi) => kpi !== KPIS.OCCUPANCY_MIX);
  }

  getComparisonSetBreakdowns(
    breakdownsByComparisonSet: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
    comparisonSet: ComparisonSet,
  ): BeniBreakdown[] {
    if (!breakdownsByComparisonSet) {
      return [];
    }

    const comparisonSetBreakdowns = breakdownsByComparisonSet[comparisonSet];
    if (!comparisonSetBreakdowns) {
      return [];
    }

    return comparisonSetBreakdowns;
  }

  <template>
    <PrismCard
      @contentLayout="noPadding"
      class="{{styles.container}}
        ic-product-tour-trial-onboarding-performance-overview"
    >
      <:header>
        <div class="flex items-center">
          <p class="text-400--bold">
            {{t "benchmark_insight.headers.performance_overview"}}
          </p>
        </div>
      </:header>

      <:headerAfter>
        <HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateToolbox
          @dateAggregation={{@dateAggregation}}
          @exportStayDateTableTask={{this.exportStayDateTableTask}}
          @isLoading={{@showSkeleton}}
          @setDateAggregation={{@setDateAggregation}}
          @setStayDateSectionView={{@setStayDateSectionView}}
          @stayDateSectionView={{@stayDateSectionView}}
        />
      </:headerAfter>

      <:content>
        <div class={{styles.card-content}}>
          {{#if this.isChartView}}
            <div
              class={{styles.chart-view}}
              data-test-id="benchmark-insight-stay-date-section-chart-view"
            >
              <HotelBenchmarkInsightHeroChartAxisButtons
                @changeSelectedAxisOption={{@changeSelectedAxisOption}}
                @changeSelectedKpi={{@changeSelectedKpi}}
                @kpiDiffVsComparisonSetTranslation={{this.kpiDiffVsComparisonSetTranslation}}
                @oppositeKpi={{this.oppositeKpi}}
                @primaryComparisonSet={{@primaryComparisonSetType}}
                @selectedAxisOption={{@selectedAxisOption}}
                @selectedKpi={{@selectedKpi}}
                @selectedRange={{@selectedRange}}
              />

              <div class={{styles.hero-chart}}>
                {{#if @showSkeleton}}
                  <div class={{styles.hero-chart-skeleton}}>
                    <HotelBenchmarkInsightSkeleton
                      @imageHtml={{SkeletonHeroChartIllustration}}
                      @showAnimation={{@showSkeletonAnimation}}
                    />
                  </div>
                {{else}}
                  <HotelBenchmarkInsightChartsHeroChart
                    @dateAggregation={{@dateAggregation}}
                    @isOccupancySelected={{this.isOccupancySelected}}
                    @kpiDiffVsComparisonSetTranslation={{this.kpiDiffVsComparisonSetTranslation}}
                    @ownComparisonSetBreakdowns={{this.ownComparisonSetBreakdowns}}
                    @ownComparisonSetMeta={{this.ownComparisonSetMeta}}
                    @pickupTranslation={{@pickupTranslation}}
                    @primaryComparisonSetBreakdowns={{this.primaryComparisonSetBreakdowns}}
                    @primaryComparisonSetMeta={{this.primaryComparisonSetMeta}}
                    @rangeChanged={{@rangeChanged}}
                    @referencePeriod={{@referencePeriod}}
                    @secondaryComparisonSetBreakdowns={{this.secondaryComparisonSetBreakdowns}}
                    @secondaryComparisonSetMeta={{this.secondaryComparisonSetMeta}}
                    @selectedAxisOption={{@selectedAxisOption}}
                    @selectedKpi={{@selectedKpi}}
                    @selectedRange={{@selectedRange}}
                  />
                {{/if}}
              </div>
            </div>
          {{else}}
            <div
              class={{styles.table-view}}
              data-test-id="benchmark-insight-stay-date-section-table-view"
            >
              {{#if @showSkeleton}}
                <div class={{styles.table-view-skeleton}}>
                  <HotelBenchmarkInsightSkeleton
                    @imageHtml={{SkeletonTableIllustration}}
                    @showAnimation={{@showSkeletonAnimation}}
                  />
                </div>
              {{else}}
                <HotelBenchmarkInsightTablesBeniTable
                  @columns={{this.columns}}
                  @removeBorderRadius={{true}}
                  @rows={{this.rows}}
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
